import CTNUpload from '@ctn/ui/components/ctn-upload';
import { useEffect, useState, type ReactElement } from 'react';

import DocViewer from '@/components/DocViewer';
import ModalSubsanacionDocumentos from '@/components/ModalSubsanacionDocumentos';
import { useGlobalContext } from '@/context/global.context';
import useFetchDocument from '@/hooks/useFetchDocument';
import { usePdfViewer } from '@/hooks/usePdfViewer';

import { commonUpload, getIconDocumento } from '@/helpers/upload';
import { type IDocumento, type ISubsanacionDocumento } from '@/interfaces/model/don.interface';

const DocumentoSection = ({
	data,
	isShowSubsanacion,
	onCloseSubsanacion,
	onSubmitSubsanacion,
	className,
	isSubsanable,
}: {
	readonly data: ISubsanacionDocumento;
	readonly isSubsanable: boolean;
	readonly isShowSubsanacion: boolean;
	readonly className?: string;
	readonly onCloseSubsanacion: () => void;
	readonly onSubmitSubsanacion: () => void;
}): ReactElement => {
	const { documentoErroneo, documentoSubsanado, tramite, tipo, id } = data;
	const { maestrosTramite: { configuracionTramite } } = useGlobalContext();
	const [docSelected, setDocSelected] = useState<IDocumento>(documentoErroneo);

	const { fetchDocument } = useFetchDocument({ idTramite: Number(tramite.id) });
	const { isLoading, url, loadPdf } = usePdfViewer();

	const getPdfSource = async (doc: IDocumento) => {
		const id = doc?.id;
		if (!id) return undefined;

		return (await fetchDocument(id))?.data;
	};

	const onClickUpload = (doc: IDocumento) => {
		setDocSelected(doc);
		loadPdf(() => getPdfSource(doc));
	};

	useEffect(() => {
		loadPdf(() => getPdfSource(documentoErroneo));
	}, []);

	return (
		<>
			<fieldset className={className}>
				<legend className="c-pnp-heading-h4">
					{isSubsanable ? 'Documento a subsanar' : 'Datos de subsanación'}
				</legend>
				<div className="[ o-grid ] [ u-mt-16 ] [ c-pnp-form-group c-pnp-form-group--display-only ] [ o-flow ]">
					<div className="o-grid__row">
						<CTNUpload
							{...commonUpload}
							displayOnly
							noFeedBack
							condensed
							maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
							classNameField="o-grid__col-12 o-grid__col-6@portrait"
							label={isSubsanable ? 'Documento' : 'Documento a subsanar'}
							fileLoaded={
								documentoErroneo?.nombre
									? { name: documentoErroneo?.nombre, icon: getIconDocumento(documentoErroneo) }
									: undefined
							}
							onClickFile={() => onClickUpload(documentoErroneo)}
						/>
						{!isSubsanable && (
							<CTNUpload
								{...commonUpload}
								displayOnly
								noFeedBack
								condensed
								maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
								classNameField="o-grid__col-12 o-grid__col-6@portrait"
								label="Documento subsanado"
								fileLoaded={
									documentoSubsanado?.nombre
										? {
											name: documentoSubsanado?.nombre,
											icon: getIconDocumento(documentoSubsanado),
										}
										: undefined
								}
								onClickFile={() => onClickUpload(documentoSubsanado!)}
							/>
						)}
					</div>
				</div>
				<DocViewer
					mimeType={docSelected.mimeType.nombre}
					loading={isLoading}
					className="u-mt-32"
					url={url}
					fileIcon={getIconDocumento(docSelected)}
					fileName={docSelected.nombre}
				/>
			</fieldset>
			<ModalSubsanacionDocumentos
				hasErrorDoc
				data={{
					idTramite: tramite.id,
					idTipo: tipo.id,
					id,
				}}
				idTipoDocumento={documentoErroneo.tipoDocumento.id}
				isOpen={isShowSubsanacion}
				onClose={onCloseSubsanacion}
				onSubmit={() => {
					onCloseSubsanacion();
					onSubmitSubsanacion();
				}}
			/>
		</>
	);
};

export default DocumentoSection;
